import { Injectable } from '@angular/core';
import { GiftCardsAppConstant } from '@src/ib-gift-cards-constants';
import { downgradeToAJSService} from 'ib-web-commons/utils';

@Injectable({
    providedIn: 'root'
})
export class GiftCardsSessionStorageService {

    private prefix = GiftCardsAppConstant.PREFIXSTORAGE;

    setPrefix (prefix: string): void {
        this.prefix = prefix;
    }

    isSupported (): boolean {
        return typeof window.sessionStorage !== 'undefined';
    }

    /**
     * @description Iberia utility to set an object to [<b>session storage</b>]{@link https://developer.mozilla.org/es/docs/Web/API/Window/sessionStorage}.
     * Do not pass undefined or null values. If an undefined value is passed,
     * it will be converted to null to avoid errors when recovering the value and make it more consistent.
     * @param {string} key The key to set the value to. It will be prefixed by default if the config is set in the application
     * @param {unknown} value Any value to store, non-null or undefined. It will be serialized.
     * @example
     * storageService.set('key', value);
     */
    set (key: string, value: NonNullable<unknown>): void {
        const valueToSave = JSON.stringify(typeof value === 'undefined' ? null : value);
        const keyToSet = `${this.prefix}.${key}`;
        window.sessionStorage.setItem(keyToSet, valueToSave);
    }

    /**
     * @description Iberia utility to get an object from [<b>session storage</b>]{@link https://developer.mozilla.org/es/docs/Web/API/Window/sessionStorage}.
     * It uses generic type to set the typing, see examples for more info. If no value is found, it returns null consistently.
     * @param {string} key The key to get the value from. It will be prefixed by default if the config is set in the application
     * @returns The value found or null if not found.
     * @example
     * storageService.get<MyType>('key'); // returns 'value' as MyType
     * storageService.get<string>('key'); // returns 'value' as string
     */
    get<T = unknown> (key: string) {
        const keyToGet = `${this.prefix}.${key}`;
        const storagedValue = window.sessionStorage.getItem(keyToGet);
        try {
            return JSON.parse( storagedValue ?? 'null') as T;
        } catch (err) {
            return storagedValue === '' ? null : storagedValue as unknown as T;
        }
    }
    /**
     * @description Iberia utility to remove a value from [<b>session storage</b>]{@link https://developer.mozilla.org/es/docs/Web/API/Window/sessionStorage}.
     * @param {string} key The key to remove of the storage. It will be prefixed by default if the config is set in the application
     */
    remove (key: string): void {
        const keyToRemove = `${this.prefix}.${key}`;
        window.sessionStorage.removeItem(keyToRemove);
    }
    /**
     * @description Iberia utility to clear all variables that matches with a patter from [<b>session storage</b>]{@link https://developer.mozilla.org/es/docs/Web/API/Window/sessionStorage}.
     */
    clearAll (): void {
        const prefixPattern = new RegExp(`^${this.prefix}`);
        for (const key in window.sessionStorage) {
            if (prefixPattern.test(key)) {
                window.sessionStorage.removeItem(key);
            }
        }
    }
}

downgradeToAJSService('GiftCardsSessionStorageService', GiftCardsSessionStorageService, 'ib-gift-cards-service-module');
